import { createSelector } from "reselect";
import {
  deliveryInfoSelector,
  paymentModeSelector,
  personalDetailsSelector,
  localeSelector,
  numberFormatSelector,
  deliverySlotsSelector,
  userDetailsConfirmationSelector,
  completeOrderParamsSelector,
  paymentReturnChannelsWithoutDateTimeSelector,
  inCompleteOrderSelector,
  orderSelector,
  simReplacementFormSelector,
  slotDetailsSelector,
  shippingMethodDetailsSelector,
  selfPickupStationDetailsSelector,
  selectedShippingMethodSelector,
  loginEmailSelector,
  selectedNumberSelector
} from "ducks/selectors";
import { globalFormatSelector } from "./globalFormat";
import {
  addressParams,
  constructLocalizedDeliveryAddress,
  constructLocalizedDeliverySlotDetails,
  constructLocalizedDeliverySlotLabel,
  formatDisplayNumber,
  compactJoin,
  constructDeliveryDateTime,
  getChannelFromUrl,
  formatContactNumber,
  formatMessage,
  getFormattedDeliveryDateAndTime,
  constructShippingDetails,
  constructSelfPickupDetails
} from "utils/helpers";
import {
  CURRENCY_STYLES,
  DATE_STYLES,
  formatCurrency,
  formatDate
} from "utils/i18nFormatter";
import { ORDER_STATUS, SIM_TYPES, CONTRACT_ITEM_TYPES } from "utils/constants";
import orderBy from "lodash/orderBy";
import values from "lodash/values";
// import moment from "moment";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import includes from "lodash/includes";
import { getItem } from "utils/localStorage";
import {
  simReplacementFeeSelector,
  simReplacementDeliveryFeeSelector
} from "./serviceRequests";
import { currencyFormatSelector } from "./appSettings";
import { connectionDetailsSelector } from "./forms/connectionDetails";
import { selectedActiveLineSelector } from "./index";

export const isUpdateOrderSelector = createSelector(
  inCompleteOrderSelector,
  orderSelector,
  (inCompleteorder, order) =>
    !isEmpty(inCompleteorder) || get(order, "order_ref") || getItem("order_ref")
);

export const simReplacementDetailsSelector = createSelector(
  simReplacementFormSelector,
  simReplacementFeeSelector,
  simReplacementDeliveryFeeSelector,
  localeSelector,
  currencyFormatSelector,
  selectedShippingMethodSelector,
  loginEmailSelector,
  numberFormatSelector,
  selectedActiveLineSelector,
  (
    form,
    replacementFee,
    deliveryFee,
    locale,
    currency,
    shippingMethod,
    loginEmail,
    numberFormat,
    selectedTelcoLineInfo
  ) => {
    const countryCode =
      get(form, "country_code") || get(numberFormat, "country_code");
    return isEmpty(form)
      ? {}
      : {
          replacement_sim_type: get(form, "sim_type.label"),
          personal_details_nationality: get(form, "nationality.label"),
          personal_details_nric: get(form, "document_id") || get(form, "nric"),
          personal_details_id_type: get(form, "doc_type.label"),
          personal_document_expiry_date: formatDate(
            new Date(form?.document_expiry_date),
            {
              style: DATE_STYLES.SHORT
            }
          ),

          temporary_contact_number: formatDisplayNumber(
            get(form, "temporary_contact_number"),
            numberFormat,
            countryCode
          ),
          contactEmail: loginEmail,
          shipping_method: get(shippingMethod, "name"),
          contactNumber: formatDisplayNumber(
            selectedTelcoLineInfo?.value?.phone_no,
            numberFormat,
            countryCode
          ),
          sim_replacement_fee: get(replacementFee, "displayPrice"),
          delivery_fee: get(deliveryFee, "displayPrice"),
          reason: get(form, "reason.label"),
          total: formatCurrency(
            get(replacementFee, "price", 0) + get(deliveryFee, "price", 0),
            {
              style: CURRENCY_STYLES.LONG
            }
          )
        };
  }
);

export const makeSelectSimReplacementDetails = () =>
  simReplacementDetailsSelector;

export const orderDataSelector = createSelector(
  orderSelector,
  personalDetailsSelector,
  (order, personalDetails) => {
    let orderInfo = {};
    if (
      get(order, "state") === get(ORDER_STATUS, "CREATED") ||
      get(order, "state") === get(ORDER_STATUS, "PLACED")
    ) {
      const first_name = get(order, "userDetails.firstName");
      const middle_name = get(order, "userDetails.middleName");
      const last_name = get(order, "userDetails.lastName");
      const full_name = compactJoin([first_name, middle_name, last_name], " ");
      const contact_number = get(order, "userDetails.contactNumber");
      const email = get(order, "userDetails.email");
      const state = get(order, "state");
      const slotFee = get(order, "initialShipmentDetails.price");
      const [dobDay, dobMonth, dobYear] = (
        get(order, "userDetails.dateOfBirth") ?? ""
      ).split("-");
      const billing_street_name = get(order, "billingDetails.addressLine1");
      const billing_zip_code = get(order, "billingDetails.postalCode");
      const billing_state = get(order, "billingDetails.state");
      const billing_city = get(order, "billingDetails.city");
      const billing_country = get(order, "billingDetails.country");
      const billing_unit_no = get(order, "billingDetails.addressLine2");
      const billing_address_line_2 = get(order, "billingDetails.addressLine2");
      const billing_address_line_1 = get(order, "billingDetails.addressLine1");
      const billing_province = get(order, "billingDetails.province");
      const billing_area = get(order, "billingDetails.area");

      const delivery_zip_code = get(order, "initialShipmentDetails.postalCode");
      const delivery_state = get(order, "initialShipmentDetails.state");
      const delivery_city = get(order, "initialShipmentDetails.city");
      const delivery_country = get(order, "initialShipmentDetails.country");
      const delivery_address_line_1 = get(
        order,
        "initialShipmentDetails.addressLine1"
      );
      const delivery_address_line_2 = get(
        order,
        "initialShipmentDetails.addressLine2"
      );
      const delivery_province = get(order, "initialShipmentDetails.province");
      const delivery_area = get(order, "initialShipmentDetails.area");

      const delivery_unit_no = get(
        order,
        "initialShipmentDetails.addressLine2"
      );
      const delivery_street_name = get(
        order,
        "initialShipmentDetails.addressLine1"
      );

      const slot = get(order, "initialShipmentDetails.slot");

      const delivery_slot_full_data = get(
        personalDetails,
        "delivery_slot_full_data"
      );
      const delivery_slot_label = get(personalDetails, "delivery_slot_label");

      const billingDetails = {
        addressLine1: billing_street_name,
        postalCode: billing_zip_code,
        state: billing_state,
        city: billing_city,
        country: billing_country
      };

      let same_as_billing_address;

      const contractType = find(get(order, "items"), {
        itemType: get(CONTRACT_ITEM_TYPES, "NEW_NUMBER", "")
      })
        ? "New"
        : "PORT_IN";
      const simType = find(get(order, "items"), { sku: "8888888888888" })
        ? "E_SIM"
        : "PHYSICAL_SIM";

      if (simType === SIM_TYPES.PHYSICAL_SIM) {
        same_as_billing_address = true;
        for (const key in billingDetails) {
          if (
            billingDetails[key] !== get(order, `initialShipmentDetails.${key}`)
          ) {
            same_as_billing_address = false;
            break;
          }
        }
      }

      const nationality = {
        value: get(order, "userDetails.nationality", "")
      };
      const identification_number = get(
        order,
        "userDetails.identificationNumber",
        ""
      );
      const document_identificationNumberExpiryDate = get(
        order,
        "userDetails.identificationExpiryDate"
      );

      const delivery_instructions = get(
        order,
        "initialShipmentDetails.specialInstructions",
        ""
      );

      const delivery_landmark = get(
        order,
        "initialShipmentDetails.landmark",
        ""
      );

      const selected_number = (order?.items || []).find(
        item => item?.itemType === CONTRACT_ITEM_TYPES.NEW_NUMBER
      );

      const port_number = (order?.items || []).find(
        item => item?.itemType === CONTRACT_ITEM_TYPES.PORTIN_NUMBER
      );

      orderInfo = {
        full_name,
        middle_name,
        last_name,
        first_name,
        contact_number,
        email,
        billing_street_name,
        billing_zip_code,
        billing_state,
        billing_city,
        billing_country,
        billing_unit_no,
        billing_address_line_1,
        billing_address_line_2,
        billing_province,
        billing_area,
        contractType,
        simType,
        state,
        slotFee,
        slot,
        delivery_street_name,
        delivery_zip_code,
        delivery_state,
        delivery_city,
        delivery_province,
        delivery_area,
        delivery_country,
        delivery_address_line_1,
        delivery_address_line_2,
        delivery_unit_no,
        same_as_billing_address,
        delivery_slot_full_data,
        delivery_slot_label,
        dobDay: {
          value: dobDay
        },
        dobMonth: {
          value: dobMonth
        },
        dobYear: {
          value: dobYear
        },
        nationality,
        identification_number,
        document_identificationNumberExpiryDate,
        delivery_instructions,
        delivery_landmark,
        selected_number: get(selected_number, "identifiedBy", ""),
        port_number: get(port_number, "identifiedBy", ""),
        port_donor: get(port_number, "properties.CURRENT_TELCO", "")
      };
    }
    return orderInfo;
  }
);
export const makeSelectDeliveryDetailsItems = () =>
  createSelector(
    orderDataSelector,
    deliveryInfoSelector,
    personalDetailsSelector,
    userDetailsConfirmationSelector,
    completeOrderParamsSelector(),
    localeSelector,
    numberFormatSelector,
    deliverySlotsSelector,
    paymentReturnChannelsWithoutDateTimeSelector,
    simReplacementDetailsSelector,
    slotDetailsSelector,
    shippingMethodDetailsSelector,
    selfPickupStationDetailsSelector,
    connectionDetailsSelector,
    selectedNumberSelector,
    globalFormatSelector,
    (
      order,
      deliveryInfo,
      personalDetails,
      userDetailsConfirmation,
      orderParams,
      locale,
      numberFormat,
      deliverySlots,
      paymentReturnChannelsWithoutDateTime,
      lostSimDetails,
      slotDetails,
      shippingMethodDetails,
      selfPickupStationDetails,
      connectionDetails,
      selectedNumberData
    ) => {
      const formData = isEmpty(order)
        ? isEmpty(deliveryInfo)
          ? isEmpty(personalDetails)
            ? isEmpty(lostSimDetails)
              ? userDetailsConfirmation
              : lostSimDetails
            : personalDetails
          : deliveryInfo
        : order;
      if (isEmpty(formData)) return {};
      const full_name =
        get(formData, "full_name") ||
        get(orderParams, "user_detail.full_name") ||
        get(personalDetails, "full_name");
      const first_name =
        get(formData, "first_name") ||
        get(orderParams, "user_detail.first_name") ||
        get(personalDetails, "first_name");
      const middle_name =
        get(formData, "middle_name") ||
        get(orderParams, "user_detail.middle_name") ||
        get(personalDetails, "middle_name");
      const last_name =
        get(formData, "last_name") ||
        get(orderParams, "user_detail.last_name") ||
        get(personalDetails, "last_name");
      const name =
        full_name || compactJoin([first_name, middle_name, last_name], " ");
      const countryCode =
        get(formData, "country_code") || get(numberFormat, "country_code");
      const storeId = get(personalDetails, "storeId");
      const birthDate =
        get(formData, "dobDay") ||
        get(orderParams, "user_detail.dobDay") ||
        get(personalDetails, "dobDay");
      const birthMonth =
        get(formData, "dobMonth") ||
        get(orderParams, "user_detail.dobMonth") ||
        get(personalDetails, "dobMonth");
      const birthYear =
        get(formData, "dobYear") ||
        get(orderParams, "user_detail.dobYear") ||
        get(personalDetails, "dobYear");
      const dateOfBirth = formatDate(
        new Date(
          `${birthYear?.value}/${birthMonth?.value}/${birthDate?.value}`
        ),
        {
          style: DATE_STYLES.SHORT
        }
      );
      const sameAsBillingAddress = get(
        formData,
        "same_as_billing_address",
        false
      );

      const contact_number = get(
        formData,
        "contact_number",
        get(
          orderParams,
          "user_detail.phone_number",
          get(personalDetails, "contact_number", "")
        )
      );
      const contactNumber = formatContactNumber(
        contact_number,
        numberFormat,
        countryCode
      );
      const contactEmail = get(
        formData,
        "email",
        get(personalDetails, "email", "")
      );

      const identityDocumentNumberExpiryDate = formatDate(
        new Date(
          get(formData, "document_identificationNumberExpiryDate")
            ?.split("-")
            ?.reverse()
            ?.join("-")
        ),
        {
          style: DATE_STYLES.SHORT
        }
      );

      const identityDocumentNumber = get(formData, "identification_number");
      const slotDateTime =
        get(formData, "delivery_slot_full_data") ||
        get(deliveryInfo, "delivery_slot_full_data") ||
        "";
      const deliverySlotLabel =
        get(formData, "delivery_slot_label") ||
        get(deliveryInfo, "delivery_slot_label") ||
        "";
      const slotId =
        get(formData, "delivery_slot_id") ||
        get(deliveryInfo, "delivery_slot_id") ||
        0;
      const slotData = find(deliverySlots, {
        id: slotId,
        date_time: slotDateTime
      });
      const hideDeliveryDateTime = includes(
        paymentReturnChannelsWithoutDateTime,
        getChannelFromUrl()
      );
      const slot = constructLocalizedDeliverySlotDetails(slotDateTime, locale);
      const { deliveryDate, deliveryTime } = slot;
      const deliveryDateAndTime = getFormattedDeliveryDateAndTime(
        get(formData, "slot.date", "")
          ?.split("-")
          ?.reverse()
          ?.join("-") || deliveryDate,
        deliverySlotLabel,
        slotDetails,
        locale
      );
      const endDate = get(slotData, "range_end_date");
      const deliveryEndDate = endDate
        ? constructLocalizedDeliverySlotLabel(endDate, locale)
        : null;
      const billingAddressDetails = addressParams(formData, "billing");
      const billingAddress = constructLocalizedDeliveryAddress(
        billingAddressDetails,
        locale,
        "delivery_address_label"
      );

      const deliveryAddressDetails = addressParams(
        formData,
        sameAsBillingAddress ? "billing" : "delivery"
      );
      const deliveryAddress = constructLocalizedDeliveryAddress(
        deliveryAddressDetails,
        locale,
        "delivery_address_label"
      );
      const deliveryDateTime = hideDeliveryDateTime
        ? undefined
        : constructDeliveryDateTime({
            deliveryDate: compactJoin([deliveryDate, deliveryEndDate], " - "),
            deliveryTime,
            locale
          });
      const selected_number =
        get(formData, "selected_number") || get(selectedNumberData, "number");
      const selectedNumber = formatDisplayNumber(selected_number, numberFormat);
      const portNumber =
        get(formData, "port_number") ||
        get(orderParams, "portin_detail.port_number");
      const isPortInRequest = get(formData, "is_port_in_request", portNumber);
      const portDonor =
        get(formData, "port_donor") ||
        get(orderParams, "portin_detail.port_donor");
      const portinNumber = portNumber
        ? formatDisplayNumber(portNumber, numberFormat)
        : "";
      const portinNumberFromDonorMessage = get(
        locale,
        "portin_number_from_donor",
        "{portin_number} from {donor}"
      );
      const portinNumberFromDonor =
        portinNumber && portDonor
          ? formatMessage(portinNumberFromDonorMessage, {
              portin_number: portinNumber,
              donor: portDonor
            })
          : portinNumber;
      const shippingDetails = constructShippingDetails(
        shippingMethodDetails,
        locale
      );
      const selfPickupDetails = constructSelfPickupDetails(
        selfPickupStationDetails,
        locale
      );
      const nationality = get(formData, "nationality.value", "");
      const deliveryInstructions = get(formData, "delivery_instructions", "");
      const deliveryLandMark = get(formData, "delivery_landmark", "");

      return {
        name,
        storeId,
        isPortInRequest,
        portDonor,
        connectionDetails,
        portinNumber,
        portinNumberFromDonor,
        selectedNumber,
        alternateContactNumber: contactNumber,
        currentContactNumber: contactNumber,
        contactNumberWithoutPrefix: formatDisplayNumber(
          contact_number,
          numberFormat
        ),
        firstName: first_name,
        middleName: middle_name,
        lastName: last_name,
        dateOfBirth,
        contactNumber,
        contactEmail,
        identityDocumentNumberExpiryDate,
        identityDocumentNumber,
        deliveryAddress,
        billingAddress,
        deliveryDate,
        deliveryDateAndTime,
        deliveryInstructions,
        deliveryLandMark,
        nationality,
        simType: get(formData, "simType") || get(connectionDetails, "sim_type"),
        contractType:
          get(formData, "contractType") ||
          get(connectionDetails, "number_type"),
        deliveryTime,
        deliveryDateTime,
        deliverySlotLabel,
        ...deliveryAddressDetails,
        sameAsBillingAddress,
        shippingDetails,
        selfPickupDetails,
        ...lostSimDetails
      };
    }
  );

export const makeSelectPaymentModes = () =>
  createSelector(paymentModeSelector, paymentModes => {
    return orderBy(values(paymentModes), ["display_sequence"], ["asc"])
      .filter(paymentMode => paymentMode.enable)
      .map(method => {
        return {
          ...method,
          id: method.action,
          label: method.label,
          value: method.action
        };
      });
  });

export const makeSelectIsUpdateOrder = () => isUpdateOrderSelector;
